import { PopoverContentProps, PopoverContent } from '../../designSystem/Popover';
import { MenuItem, MenuItemBaseProps } from '../../designSystem/MenuItem';
import { cn } from '../../lib';
import { memo, useCallback, useContext } from 'react';
import { SearchInputContext } from './SearchInput.context';
import { XIcon } from '@page/icons';

export const SearchInputCurrentValueSuggestion = ({
  value,
  onClick,
  className,
  addCurrentValueText = 'Search for',
}: {
  value?: string;
  onClick?: MenuItemBaseProps['onClick'];
  className?: string;
  addCurrentValueText?: string;
}) => {
  const { handleAddTag } = useContext(SearchInputContext);

  const handleClick = useCallback<NonNullable<MenuItemBaseProps['onClick']>>(
    (e) => {
      onClick?.(e);
      if (value) {
        handleAddTag(value);
      }
    },
    [handleAddTag, onClick, value],
  );

  return (
    <MenuItem onClick={handleClick} className={cn('justify-between font-body-sm font-medium', className)}>
      <span>
        {addCurrentValueText} &quot;{value}&quot;
      </span>
      <div className="py-[3px] px-2 bg-bg-inter-secondary-default rounded-sm">Enter</div>
    </MenuItem>
  );
};

export interface SearchInputPopoverProps {
  inputValue: string;
  tags: string[];
  renderSuggestions?: (searchValue: string, onSelectSuggestion: (suggestion: string) => void) => React.ReactNode;
  content?: React.ReactNode;
  className?: string;
  align?: PopoverContentProps['align'];
}

export const SearchInputPopover = memo(
  ({ inputValue, tags, renderSuggestions, content, className, align = 'end' }: SearchInputPopoverProps) => {
    const { handleAddTag, handleRemoveTag } = useContext(SearchInputContext);
    return (
      <PopoverContent
        className={cn(
          'p-1.5 w-auto flex flex-col gap-0.5 min-w-60 font-body-sm font-medium text-text-secondary fill-text-secondary max-h-[400px] overflow-y-auto',
          className,
        )}
        align={align}
        sideOffset={4}
        withinPortal
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        {content ?? (
          <>
            {inputValue && (
              <SearchInputCurrentValueSuggestion
                onClick={() => {
                  handleAddTag(inputValue);
                }}
                value={inputValue}
              />
            )}

            {renderSuggestions?.(inputValue, handleAddTag)}

            {tags.length > 0 && (
              <>
                {tags.map((tag) => (
                  <MenuItem
                    key={tag}
                    className="justify-between font-body-sm font-medium text-text-secondary"
                    onClick={() => handleRemoveTag(tag)}
                  >
                    <span>{tag}</span>
                    <XIcon className="size-4" />
                  </MenuItem>
                ))}
              </>
            )}
          </>
        )}
      </PopoverContent>
    );
  },
);
